/* global window, document */
(function () {
  const STYLE_ID = 'soothe-toast-styles';
  const ROOT_ID = 'soothe-toast-root';
  const TOAST_CLASS = 'soothe-toast';
  const VISIBLE_CLASS = 'is-visible';

  const VARIANT_THEMES = {
    success: { background: '#fffaf4', color: '#2E4C40', border: '#2E4C40' },
    error: { background: '#fff5f4', color: '#B3261E', border: '#B3261E' },
    info: { background: '#e0e7ff', color: '#1E3A8A', border: '#1E3A8A' },
    warning: { background: '#fef3c7', color: '#92400E', border: '#92400E' }
  };

  const DEFAULT_OPTIONS = {
    duration: 5000,
    variant: 'success',
    dismissible: true
  };

  function ensureStyles () {
    if (document.getElementById(STYLE_ID)) {
      return;
    }

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #${ROOT_ID} {
        position: fixed;
        top: 24px;
        right: 24px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        z-index: 2000;
        pointer-events: none;
      }

      @media (max-width: 749px) {
        #${ROOT_ID} {
          left: 16px;
          right: 16px;
        }
      }

      .${TOAST_CLASS} {
        min-width: 240px;
        max-width: 360px;
        border-radius: 12px;
        padding: 1rem 1.25rem;
        border: 1px solid transparent;
        box-shadow: 0 16px 32px rgba(17, 24, 39, 0.15);
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        opacity: 0;
        transform: translateY(-10px);
        transition: opacity 0.25s ease, transform 0.25s ease;
        pointer-events: auto;
        font-family: var(--font-body-family, 'Family', sans-serif);
      }

      .${TOAST_CLASS}.${VISIBLE_CLASS} {
        opacity: 1;
        transform: translateY(0);
      }

      .${TOAST_CLASS}__message {
        flex: 1;
        font-size: 1.4rem;
        line-height: 1.4;
        margin: 0;
      }

      .${TOAST_CLASS}__close {
        background: transparent;
        border: none;
        color: inherit;
        cursor: pointer;
        font-size: 1.6rem;
        line-height: 1;
        padding: 0;
      }

      .${TOAST_CLASS}__close:focus {
        outline: 2px solid currentColor;
        outline-offset: 2px;
      }

      .${TOAST_CLASS}--success {
        background: ${VARIANT_THEMES.success.background};
        color: ${VARIANT_THEMES.success.color};
        border-color: ${VARIANT_THEMES.success.border};
      }

      .${TOAST_CLASS}--error {
        background: ${VARIANT_THEMES.error.background};
        color: ${VARIANT_THEMES.error.color};
        border-color: ${VARIANT_THEMES.error.border};
      }

      .${TOAST_CLASS}--info {
        background: ${VARIANT_THEMES.info.background};
        color: ${VARIANT_THEMES.info.color};
        border-color: ${VARIANT_THEMES.info.border};
      }

      .${TOAST_CLASS}--warning {
        background: ${VARIANT_THEMES.warning.background};
        color: ${VARIANT_THEMES.warning.color};
        border-color: ${VARIANT_THEMES.warning.border};
      }
    `;

    document.head.appendChild(style);
  }

  function ensureRoot () {
    let root = document.getElementById(ROOT_ID);
    if (!root) {
      root = document.createElement('div');
      root.id = ROOT_ID;
      root.setAttribute('role', 'status');
      root.setAttribute('aria-live', 'polite');
      root.setAttribute('aria-atomic', 'true');
      document.body.appendChild(root);
    }
    return root;
  }

  function removeToast (toast) {
    if (!toast) return;
    toast.classList.remove(VISIBLE_CLASS);
    window.setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 250);
  }

  function buildToast (options) {
    const { message, variant, dismissible } = options;

    const toast = document.createElement('div');
    toast.className = `${TOAST_CLASS} ${TOAST_CLASS}--${variant}`;
    toast.setAttribute('role', 'status');

    const messageEl = document.createElement('p');
    messageEl.className = `${TOAST_CLASS}__message`;
    messageEl.textContent = message;
    toast.appendChild(messageEl);

    if (dismissible) {
      const closeBtn = document.createElement('button');
      closeBtn.className = `${TOAST_CLASS}__close`;
      closeBtn.type = 'button';
      closeBtn.setAttribute('aria-label', 'Dismiss notification');
      closeBtn.innerHTML = '&times;';
      closeBtn.addEventListener('click', () => removeToast(toast));
      toast.appendChild(closeBtn);
    }

    return toast;
  }

  function showToast (messageOrOptions, overrideOptions) {
    const options = normalizeOptions(messageOrOptions, overrideOptions);

    ensureStyles();
    const root = ensureRoot();

    const toast = buildToast(options);
    root.appendChild(toast);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toast.classList.add(VISIBLE_CLASS);
      });
    });

    if (options.duration > 0) {
      window.setTimeout(() => removeToast(toast), options.duration);
    }

    return toast;
  }

  function normalizeOptions (messageOrOptions, overrideOptions) {
    let resolved = {};

    if (typeof messageOrOptions === 'string') {
      resolved = { ...overrideOptions, message: messageOrOptions };
    } else {
      resolved = { ...messageOrOptions };
    }

    const variant = resolved.variant && VARIANT_THEMES[resolved.variant]
      ? resolved.variant
      : DEFAULT_OPTIONS.variant;

    return {
      ...DEFAULT_OPTIONS,
      ...resolved,
      variant,
      duration: typeof resolved.duration === 'number' ? resolved.duration : DEFAULT_OPTIONS.duration,
      dismissible: resolved.dismissible !== undefined ? resolved.dismissible : DEFAULT_OPTIONS.dismissible
    };
  }

  const api = {
    show: showToast,
    success (message, options) {
      return showToast(message, { ...options, variant: 'success' });
    },
    error (message, options) {
      return showToast(message, { ...options, variant: 'error' });
    },
    info (message, options) {
      return showToast(message, { ...options, variant: 'info' });
    },
    warning (message, options) {
      return showToast(message, { ...options, variant: 'warning' });
    },
    dismissAll () {
      const root = document.getElementById(ROOT_ID);
      if (!root) return;
      Array.from(root.children).forEach((toast) => removeToast(toast));
    }
  };

  window.SootheToast = api;
})();
